import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import { ArrowRight, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import IdeaLabPromo from "@/components/IdeaLabPromo";

const SECTORS = ["SaaS", "Professional services", "Retail / E-commerce", "Healthcare", "Manufacturing", "Non-profit", "Other"];
const TIMEFRAMES = ["ASAP", "This month", "This quarter", "Exploring"];
const BUDGETS = ["£300 – £500", "£500 – £1k", "£1k – £5k", "£5k – £15k", "£15k+", "Not sure yet"];

export default function Contact() {
  const qs = new URLSearchParams(useLocation().search);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    role: "",
    sector: "",
    website: "",
    pain_point: "",
    desired_outcome: "",
    interest_area: qs.get("interest") || "",
    timeframe: "",
    budget: "",
    source: "contact",
    consent_privacy: false,
    consent_marketing: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent_privacy) {
      toast.error("Please tick the privacy consent to continue.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/leads", form);
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not submit. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="app-container py-32 text-center" data-testid="contact-success">
        <CheckCircle size={48} weight="fill" className="text-[hsl(var(--appistry-signal))] mx-auto mb-6" />
        <h1 className="text-4xl md:text-5xl font-black tracking-tighter mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>Thanks — we've got it.</h1>
        <p className="text-lg text-zinc-600 max-w-lg mx-auto">Expect a human reply — not a bot, not a drip campaign.</p>
      </div>
    );
  }

  return (
    <div data-testid="contact-page">
      <section className="border-b border-zinc-200">
        <div className="app-container py-20 md:py-24">
          <p className="overline mb-6">Contact</p>
          <h1 className="text-5xl md:text-6xl font-black tracking-tighter leading-none max-w-3xl" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Describe the friction. We'll reply with a plan.
          </h1>
          <p className="text-lg text-zinc-600 mt-6 max-w-xl">
            The more we know up front, the sharper our reply. Fill in as much as is useful — nothing is required except the basics.
          </p>
        </div>
      </section>

      <section>
        <div className="app-container py-16 max-w-3xl">
          <form onSubmit={submit} className="space-y-6" data-testid="contact-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Full name" required>
                <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input-appistry" data-testid="contact-name" />
              </Field>
              <Field label="Work email" required>
                <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="input-appistry" data-testid="contact-email" />
              </Field>
              <Field label="Phone">
                <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="input-appistry" data-testid="contact-phone" />
              </Field>
              <Field label="Company">
                <input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="input-appistry" data-testid="contact-company" />
              </Field>
              <Field label="Your role">
                <input value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="input-appistry" data-testid="contact-role" />
              </Field>
              <Field label="Website">
                <input value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} className="input-appistry" placeholder="https://…" data-testid="contact-website" />
              </Field>
              <Field label="Sector">
                <select value={form.sector} onChange={(e) => setForm({ ...form, sector: e.target.value })} className="input-appistry" data-testid="contact-sector">
                  <option value="">Select…</option>
                  {SECTORS.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </Field>
              <Field label="Timeframe">
                <select value={form.timeframe} onChange={(e) => setForm({ ...form, timeframe: e.target.value })} className="input-appistry" data-testid="contact-timeframe">
                  <option value="">Select…</option>
                  {TIMEFRAMES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </Field>
            </div>

            <Field label="What problem are you trying to solve?" required>
              <textarea required rows={3} value={form.pain_point} onChange={(e) => setForm({ ...form, pain_point: e.target.value })} className="input-appistry" data-testid="contact-pain" />
            </Field>
            <Field label="What would 'good' look like?">
              <textarea rows={3} value={form.desired_outcome} onChange={(e) => setForm({ ...form, desired_outcome: e.target.value })} className="input-appistry" data-testid="contact-outcome" />
            </Field>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Interest area">
                <input value={form.interest_area} onChange={(e) => setForm({ ...form, interest_area: e.target.value })} className="input-appistry" placeholder="e.g. intake, reporting" data-testid="contact-interest" />
              </Field>
              <Field label="Budget (indicative)">
                <select value={form.budget} onChange={(e) => setForm({ ...form, budget: e.target.value })} className="input-appistry" data-testid="contact-budget">
                  <option value="">Select…</option>
                  {BUDGETS.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </Field>
            </div>

            <div className="space-y-3 pt-2">
              <label className="flex items-start gap-3 cursor-pointer text-sm text-zinc-700">
                <input type="checkbox" checked={form.consent_privacy} onChange={(e) => setForm({ ...form, consent_privacy: e.target.checked })} className="mt-1" data-testid="contact-consent-privacy" />
                I'm happy for Appistry to store these details and contact me about this enquiry.
              </label>
              <label className="flex items-start gap-3 cursor-pointer text-sm text-zinc-700">
                <input type="checkbox" checked={form.consent_marketing} onChange={(e) => setForm({ ...form, consent_marketing: e.target.checked })} className="mt-1" data-testid="contact-consent-marketing" />
                Occasionally, send me practical ideas (nothing salesy).
              </label>
            </div>

            <button disabled={submitting} className="btn-signal disabled:opacity-60" data-testid="contact-submit">
              {submitting ? "Sending…" : "Send enquiry"} <ArrowRight size={16} weight="bold" />
            </button>
          </form>
        </div>
      </section>

      <IdeaLabPromo variant="dark" />

      <style>{`
        .input-appistry {
          width: 100%;
          background: #fff;
          border: 1px solid hsl(var(--appistry-border));
          padding: 0.8rem 1rem;
          font-family: 'Manrope', sans-serif;
          font-size: 0.95rem;
          transition: border-color 150ms ease, box-shadow 150ms ease;
        }
        .input-appistry:focus {
          outline: none;
          border-color: hsl(var(--appistry-signal));
          box-shadow: 0 0 0 3px rgba(225,29,72,0.12);
        }
      `}</style>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="text-sm font-semibold text-zinc-800 mb-2 block">
        {label}{required && <span className="text-[hsl(var(--appistry-signal))] ml-1">*</span>}
      </span>
      {children}
    </label>
  );
}
